import { useState, useEffect } from "react";
import { Link } from "wouter";
import { useAuth } from "@/context/auth";
import { useBranch } from "@/context/branch";
import { formatCurrency } from "@/lib/currency";
import { readJsonArrayOrEmpty } from "@/lib/http";
import { sortSalesNewestFirst } from "@/lib/sales-order";
import { DashboardMoneyToggle, useDashboardMoneyVisibility } from "./dashboard-money-visibility";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  ShoppingBag, Receipt, TrendingUp, Users, BarChart3,
  RefreshCcw, ArrowRight, Package,
} from "lucide-react";

const BASE = import.meta.env.BASE_URL.replace(/\/$/, "");

function StatCard({ label, value, sub, icon: Icon, color }: {
  label: string; value: string; sub?: string; icon: React.ElementType; color: string;
}) {
  return (
    <div className="bg-card border border-border rounded-xl p-5">
      <div className="flex items-start justify-between mb-3">
        <span className="text-sm text-muted-foreground">{label}</span>
        <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${color}`}>
          <Icon className="w-4 h-4" />
        </div>
      </div>
      <div className="text-2xl font-bold">{value}</div>
      {sub && <div className="text-xs text-muted-foreground mt-1">{sub}</div>}
    </div>
  );
}

export default function FashionDashboard() {
  const { user, store } = useAuth();
  const { activeBranchId, activeBranch } = useBranch();
  const branchQuery = activeBranchId ? `?branchId=${activeBranchId}` : "";
  const isOwner = user?.role === "admin" || user?.role === "superadmin";
  const isManager = user?.role === "manager" || isOwner;
  const { moneyVisible, toggleMoneyVisibility, maskMoney } = useDashboardMoneyVisibility();

  const [stats, setStats] = useState({ todayRevenue: 0, todayTx: 0, monthRevenue: 0, totalProducts: 0, totalCustomers: 0, returnsThisMonth: 0 });
  const [bestSellers, setBestSellers] = useState<{ productId: number; productName: string; totalSold: number; totalRevenue: number }[]>([]);
  const [recentSales, setRecentSales] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const lagosDate = (value: Date | string) => new Date(value).toLocaleDateString("en-CA", { timeZone: "Africa/Lagos" });
        const today = lagosDate(new Date());
        const currentMonth = today.slice(0, 7);
        const [salesRes, productsRes, customersRes, returnsRes, bestRes] = await Promise.allSettled([
          fetch(`${BASE}/api/sales${branchQuery}`, { credentials: "include", cache: "no-store" }),
          fetch(`${BASE}/api/products${branchQuery}`, { credentials: "include" }),
          fetch(`${BASE}/api/customers`, { credentials: "include" }),
          fetch(`${BASE}/api/returns${branchQuery}`, { credentials: "include" }),
          fetch(`${BASE}/api/dashboard/top-products${branchQuery}`, { credentials: "include" }),
        ]);
        const sales = salesRes.status === "fulfilled" ? await readJsonArrayOrEmpty<any>(salesRes.value) : [];
        const products = productsRes.status === "fulfilled" ? await readJsonArrayOrEmpty<any>(productsRes.value) : [];
        const allCustomers = customersRes.status === "fulfilled" ? await readJsonArrayOrEmpty<any>(customersRes.value) : [];
        const branchCustomerIds = new Set((sales as any[]).map((s: any) => s.customerId).filter(Boolean));
        const customers = activeBranchId
          ? (allCustomers as any[]).filter((c: any) => branchCustomerIds.has(c.id))
          : allCustomers;
        const returns = returnsRes.status === "fulfilled" ? await readJsonArrayOrEmpty<any>(returnsRes.value) : [];
        const best = bestRes.status === "fulfilled" ? await readJsonArrayOrEmpty<any>(bestRes.value) : [];
        const newestSales = sortSalesNewestFirst(sales);
        const todaySales = newestSales.filter((s: any) => lagosDate(s.createdAt) === today);
        const monthSales = newestSales.filter((s: any) => lagosDate(s.createdAt).startsWith(currentMonth));
        const monthReturns = (returns as any[]).filter((r: any) => lagosDate(r.createdAt).startsWith(currentMonth));
        setStats({
          todayRevenue: todaySales.reduce((s: number, x: any) => s + Number(x.totalAmount ?? x.total ?? 0), 0),
          todayTx: todaySales.length,
          monthRevenue: monthSales.reduce((s: number, x: any) => s + Number(x.totalAmount ?? x.total ?? 0), 0),
          totalProducts: (products as any[]).length,
          totalCustomers: (customers as any[]).length,
          returnsThisMonth: monthReturns.length,
        });
        setBestSellers((best as any[]).slice(0, 5));
        setRecentSales(newestSales.slice(0, 5));
      } finally { setLoading(false); }
    };
    load();
  }, [branchQuery]);

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <span className="text-2xl">👗</span>
            <h1 className="text-2xl font-bold">{store?.name || "My Store"}</h1>
            <Badge className="bg-purple-100 text-purple-700 border-purple-200">Fashion</Badge>
            <Badge variant="outline" className="capitalize">{user?.role}</Badge>
          </div>
          <p className="text-muted-foreground text-sm">Fashion & Clothing Dashboard · {activeBranch?.name ?? "All Branches"} · {new Date().toLocaleDateString("en-NG", { weekday: "long", day: "numeric", month: "long" })}</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <DashboardMoneyToggle visible={moneyVisible} onToggle={toggleMoneyVisibility} />
          <Link href="/customers"><Button variant="outline" size="sm" className="gap-1.5"><Users className="w-3.5 h-3.5" />Customers</Button></Link>
          <Link href="/sales/new"><Button size="sm" className="gap-1.5 bg-purple-600 hover:bg-purple-700"><ShoppingBag className="w-3.5 h-3.5" />New Sale</Button></Link>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard label="Today's Revenue" value={maskMoney(formatCurrency(stats.todayRevenue))} icon={TrendingUp} color="bg-purple-100 text-purple-600" sub={`${stats.todayTx} sales`} />
        <StatCard label="This Month" value={maskMoney(formatCurrency(stats.monthRevenue))} icon={BarChart3} color="bg-blue-100 text-blue-600" />
        <StatCard label="Total Customers" value={String(stats.totalCustomers)} icon={Users} color="bg-pink-100 text-pink-600" />
        <StatCard label="Returns (Month)" value={String(stats.returnsThisMonth)} icon={RefreshCcw} color="bg-amber-100 text-amber-600" />
      </div>

      {/* Quick Actions */}
      <div>
        <h2 className="text-sm font-medium text-muted-foreground uppercase tracking-wide mb-3">Quick Actions</h2>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[
            { label: "POS / New Sale", href: "/sales/new", icon: ShoppingBag, color: "bg-purple-50 border-purple-200 hover:bg-purple-100" },
            { label: "Expenses", href: "/expenses", icon: Receipt, color: "bg-pink-50 border-pink-200 hover:bg-pink-100" },
            { label: "Customers", href: "/customers", icon: Users, color: "bg-blue-50 border-blue-200 hover:bg-blue-100" },
            { label: "Returns", href: "/returns", icon: RefreshCcw, color: "bg-amber-50 border-amber-200 hover:bg-amber-100" },
          ].map(a => (
            <Link key={a.label} href={a.href}>
              <div className={`flex items-center gap-3 p-4 rounded-xl border cursor-pointer transition-colors text-slate-800 ${a.color}`}>
                <a.icon className="w-5 h-5 text-slate-700" />
                <span className="text-sm font-semibold">{a.label}</span>
                <ArrowRight className="w-3.5 h-3.5 ml-auto text-slate-500" />
              </div>
            </Link>
          ))}
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Best Sellers */}
        <div className="bg-card border border-border rounded-xl p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold flex items-center gap-2"><TrendingUp className="w-4 h-4 text-purple-500" />Best Sellers</h3>
            <Link href="/sales"><span className="text-xs text-primary hover:underline cursor-pointer">All sales →</span></Link>
          </div>
          {bestSellers.length === 0 ? (
            <div className="text-center py-6">
              <Package className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
              <p className="text-sm text-muted-foreground">No sales recorded yet</p>
              <Link href="/sales/new"><Button size="sm" className="mt-3 bg-purple-600 hover:bg-purple-700">Record First Sale</Button></Link>
            </div>
          ) : (
            <div className="space-y-2">
              {bestSellers.map((p, i) => (
                <div key={p.productId} className="flex items-center justify-between py-2 border-b border-border/50 last:border-0">
                  <div className="flex items-center gap-3 min-w-0">
                    <span className="w-5 h-5 rounded-full bg-purple-100 text-purple-700 text-xs font-bold flex items-center justify-center shrink-0">{i + 1}</span>
                    <div className="min-w-0">
                      <div className="text-sm font-medium truncate">{p.productName}</div>
                      <div className="text-xs text-muted-foreground">{p.totalSold} unit{p.totalSold === 1 ? "" : "s"} sold</div>
                    </div>
                  </div>
                  <div className="text-sm font-semibold text-right shrink-0">{maskMoney(formatCurrency(p.totalRevenue))}</div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Recent Sales */}
        <div className="bg-card border border-border rounded-xl p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold">Recent Sales</h3>
            <Link href="/sales"><span className="text-xs text-primary hover:underline cursor-pointer">View all →</span></Link>
          </div>
          {recentSales.length === 0 ? (
            <div className="text-center py-8">
              <ShoppingBag className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
              <p className="text-sm text-muted-foreground">No sales recorded yet</p>
              <Link href="/sales/new"><Button size="sm" className="mt-3 bg-purple-600 hover:bg-purple-700">Record First Sale</Button></Link>
            </div>
          ) : (
            <div className="space-y-2">
              {recentSales.map((s: any) => (
                <div key={s.id} className="flex items-center justify-between py-2 border-b border-border/50 last:border-0">
                  <div>
                    <div className="text-sm font-medium">{s.customerName ?? "Walk-in Customer"}</div>
                    <div className="text-xs text-muted-foreground">{s.receiptNumber} · {new Date(s.createdAt).toLocaleString("en-NG", { timeZone: "Africa/Lagos", day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}</div>
                  </div>
                  <div className="text-sm font-semibold">{maskMoney(formatCurrency(Number(s.totalAmount ?? s.total ?? 0)))}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {isOwner && (
        <div className="bg-muted/40 border border-border rounded-xl p-5">
          <h3 className="font-semibold mb-3 flex items-center gap-2"><BarChart3 className="w-4 h-4 text-purple-500" />Owner Analytics</h3>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {[
              { label: "Profit & Loss", href: "/accounting/profit-loss" },
              { label: "Customer Reports", href: "/people/customer-reports" },
              { label: "Staff Performance", href: "/people/staff-performance" },
              { label: "Loyalty & Discounts", href: "/people/loyalty" },
            ].map(l => (
              <Link key={l.label} href={l.href}>
                <Button variant="outline" size="sm" className="w-full text-xs">{l.label}</Button>
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
